import { Component, OnInit } from "@angular/core";
import { AsyncPipe } from "@angular/common";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatButton } from "@angular/material/button";
import { MatDialogActions, MatDialogClose, MatDialogContent } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTab, MatTabGroup } from "@angular/material/tabs";

import { Observable, combineLatest, of } from "rxjs";
import { catchError, map, shareReplay, switchMap } from "rxjs/operators";

import { DffCookieConsentService } from "@e-forsyning/web-components/shared/dff-cookie-consent";
import { DffCookieConsentCookieWithDescription } from "@e-forsyning/web-components/shared/cookie-mapper.utils";
import { DffCookieConsentCookieListComponent } from "@e-forsyning/web-components/shared/dff-cookie-consent-cookie-list/dff-cookie-consent-cookie-list.component";
import { UmbracoHttpClient } from "@e-forsyning/common/http";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";
import { mdiArrowLeft } from "@mdi/js";
import { SvgIconRegistry } from "@dffedb/util";

import { ServiceProCookieConsentVersionService } from "./service-pro-cookie-consent-version.service";
import { mapServiceProCookiesForDialog } from "./servicepro-cookies.registry";

interface ViewModel {
    policyHtml: string;
    cookies: {
        mandatory: DffCookieConsentCookieWithDescription[];
        optional: DffCookieConsentCookieWithDescription[];
    };
}

@Component({
    selector: "service-pro-cookie-consent-dialog",
    templateUrl: "./service-pro-cookie-consent-dialog.component.html",
    styleUrls: ["./service-pro-cookie-consent-dialog.component.scss"],
    imports: [
        MatIcon,
        CdkScrollable,
        MatDialogContent,
        MatTabGroup,
        MatTab,
        DffCookieConsentCookieListComponent,
        MatDialogActions,
        MatButton,
        MatDialogClose,
        MatProgressSpinner,
        AsyncPipe
    ]
})
export class ServiceProCookieConsentDialogComponent implements OnInit {
    public side = "consent";
    public vm$: Observable<ViewModel>;

    constructor(
        private readonly cookieConsentService: DffCookieConsentService,
        private readonly indstillingerObserver: UmbracoIndstillingerObserver,
        private readonly umbracoHttpClient: UmbracoHttpClient,
        private readonly versionService: ServiceProCookieConsentVersionService,
        svgIconRegistry: SvgIconRegistry
    ) {
        svgIconRegistry.registerIcon(mdiArrowLeft, "service-pro-cookie-consent.arrow-left");
    }

    public ngOnInit(): void {
        const cookies = mapServiceProCookiesForDialog();
        this.vm$ = this.indstillingerObserver.valueChanges.pipe(
            switchMap((settings) => {
                const nodeId = this.versionService.resolvePolicyNodeId(settings);
                if (!nodeId) {
                    return of({ policyHtml: "", cookies });
                }
                return combineLatest([
                    this.umbracoHttpClient.get<string>(`GetCookieConsentText?nodeId=${nodeId}`).pipe(catchError(() => of("")))
                ]).pipe(map(([policyHtml]) => ({ policyHtml, cookies })));
            }),
            shareReplay(1)
        );
    }

    public onGotoReadMoreClick(event: Event): void {
        event.preventDefault();
        this.side = "laes-mere";
    }

    public onGotoConsentClick(event: Event): void {
        event.preventDefault();
        this.side = "consent";
    }

    public onConsentApproveClick(): void {
        this.cookieConsentService.approveCookies();
    }

    public onConsentRejectClick(): void {
        this.cookieConsentService.rejectCookies();
    }
}
